import { useEffect, useMemo, useState } from 'react';
import { PersonSummary } from '../../components/avatar';
import { capitalizarPalabras } from '../../common/upperCaseWord';
import { Pagination } from '../../components/smartTable/Pagination';
import { LiveDeviceInfoCell } from './DeviceInfoCell';
import { formatMexicoTime } from './usuariosOnline.constants';

const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

export const UsuariosOnlineLiveTable = ({ users = [], loading = false, embedded = false }) => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const totalRows = users.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / Math.max(1, pageSize)));
    const safePage = Math.min(Math.max(0, page), totalPages - 1);

    // Si el listado en vivo se reduce, no dejar la página fuera de rango.
    useEffect(() => {
        if (page !== safePage) setPage(safePage);
    }, [page, safePage]);

    const pageRows = useMemo(() => {
        const start = safePage * pageSize;
        return users.slice(start, start + pageSize);
    }, [users, safePage, pageSize]);

    return (
        <div className={`isolate flex h-full w-full min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white${embedded ? '' : ' shadow-sm'}`}>
            <div className='relative min-h-0 w-full flex-1 overflow-x-auto'>
                <table className='w-full min-w-[720px] border-collapse text-sm text-gray-800'>
                    <thead className='bg-slate-50'>
                        <tr className='border-b border-slate-200'>
                            <th className='h-11 px-4 py-0 text-left align-middle text-[11px] font-semibold uppercase tracking-wider text-slate-500'>Usuario</th>
                            <th className='h-11 px-4 py-0 text-left align-middle text-[11px] font-semibold uppercase tracking-wider text-slate-500'>Tipo</th>
                            <th className='h-11 px-4 py-0 text-left align-middle text-[11px] font-semibold uppercase tracking-wider text-slate-500'>Hora conexión</th>
                            <th className='h-11 px-4 py-0 text-left align-middle text-[11px] font-semibold uppercase tracking-wider text-slate-500'>Sesiones</th>
                            <th className='h-11 px-4 py-0 text-left align-middle text-[11px] font-semibold uppercase tracking-wider text-slate-500'>Dispositivo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && Array.from({ length: Math.min(pageSize, 4) }).map((_, index) => (
                            <tr key={`skeleton-${index}`} className='border-b border-slate-100'>
                                {Array.from({ length: 5 }).map((__, cell) => (
                                    <td key={cell} className='px-4 py-3'>
                                        <div className='h-4 w-24 rounded-md bg-slate-200/90 animate-blink' />
                                    </td>
                                ))}
                            </tr>
                        ))}

                        {!loading && totalRows === 0 && (
                            <tr>
                                <td colSpan={5} className='px-4 py-10 text-center text-sm text-gray-400'>
                                    No hay usuarios conectados actualmente.
                                </td>
                            </tr>
                        )}

                        {!loading && pageRows.map((user) => (
                            <tr key={user.userId} className='border-b border-slate-100 last:border-b-0 hover:bg-indigo-50/60'>
                                <td className='px-4 py-3'>
                                    <PersonSummary
                                        firstName={user.firstName}
                                        lastName={user.lastName}
                                        name={user.userName}
                                        meta={user.email}
                                        status='online'
                                        statusLabel='En línea'
                                        avatarSize='md'
                                    />
                                </td>
                                <td className='px-4 py-3 text-sm text-slate-700'>
                                    {capitalizarPalabras(user.typeUser)}
                                </td>
                                <td className='px-4 py-3 text-sm font-medium tabular-nums text-slate-700'>
                                    {formatMexicoTime(user.connectedAt)}
                                </td>
                                <td className='px-4 py-3'>
                                    <span className='inline-flex min-w-[2rem] items-center justify-center rounded-md border border-indigo-100 bg-indigo-50 px-2 py-1 text-sm font-semibold tabular-nums text-indigo-700'>
                                        {user.sessionCount || 0}
                                    </span>
                                </td>
                                <td className='px-4 py-3'>
                                    <LiveDeviceInfoCell sessions={user.sessions} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                page={safePage}
                pageSize={pageSize}
                totalRows={totalRows}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                disabled={loading}
                onPageChange={setPage}
                onPageSizeChange={(nextSize) => {
                    setPageSize(nextSize);
                    setPage(0);
                }}
            />
        </div>
    );
};
